import {
  useVirtualizer,
  Virtualizer,
  VirtualItem,
} from '@tanstack/react-virtual';
import {throttle} from 'lodash';
import React, {useCallback, useMemo, useRef, useEffect, useState} from 'react';

const RESIZE_THROTTLE_MS = 200;
const SCROLL_THROTTLE_MS = 100;

export interface UseVirtualizedGridOptions<T> {
  items: T[];
  scrollTopKey: string;
  overscan?: number;
  targetColumns?: number; // Target number of columns (default: 4)
  containerPadding?: number; // Container padding (default: 16px)
  gap?: number; // Gap between items (default: 16px)
  additionalRowGap?: number; // Additional gap to add to row height (default: 0px)
  defaultItemWidth?: number; // Default item width (default: 170px)
  defaultItemHeight?: number; // Default item height (default: 265px)
  aspectRatio?: number; // Width/height ratio (default: calculated from defaults)
  itemStyle?: React.CSSProperties; // Additional styles for each item (default: {})
}

export interface UseVirtualizedGridReturn<T> {
  parentRef: React.RefObject<HTMLDivElement>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  itemSize: {width: number; height: number};
  gap: {x: number; y: number};
  columns: number;
  rows: number;
  handleScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  getVirtualItems: () => VirtualItem<Element>[];
  getTotalSize: () => number;
  renderVirtualizedItems: (
    renderItem: (
      item: T,
      index: number,
      virtualRow: VirtualItem<Element>,
      virtualColumn: VirtualItem<Element>
    ) => React.ReactNode
  ) => React.ReactNode;
}

export function useVirtualizedGrid<T>({
  items,
  scrollTopKey,
  overscan = 2,
  targetColumns = 4,
  containerPadding = 16,
  gap: gapValue = 16,
  additionalRowGap = 0,
  defaultItemWidth = 170,
  defaultItemHeight = 265,
  aspectRatio = defaultItemWidth / defaultItemHeight,
  itemStyle = {},
}: UseVirtualizedGridOptions<T>): UseVirtualizedGridReturn<T> {
  const parentRef = useRef<HTMLDivElement>(null);

  // State for responsive dimensions
  const [itemSize, setItemSize] = useState({
    width: defaultItemWidth,
    height: defaultItemHeight,
  });
  const [gap, setGap] = useState({x: gapValue, y: gapValue});

  // Calculate responsive dimensions
  const calculateDimensions = useCallback(() => {
    if (!parentRef.current) {
      return;
    }

    const width = parentRef.current.clientWidth;
    if (width === 0) {
      return;
    }

    const columns = targetColumns;
    const gapX = gapValue;
    const gapY = gapValue;

    // Calculate item width: (containerWidth - totalGaps - padding) / columns
    const totalGaps = gapX * (columns - 1);
    const availableWidth = width - totalGaps - containerPadding;
    const itemWidth = Math.floor(availableWidth / columns);

    // Calculate item height maintaining aspect ratio
    const itemHeight = Math.floor(itemWidth / aspectRatio);

    setItemSize({width: itemWidth, height: itemHeight});
    setGap({x: gapX, y: gapY});
  }, [targetColumns, gapValue, containerPadding, aspectRatio]);

  // Handle resize with throttling
  const handleResize = useCallback(
    throttle(() => {
      calculateDimensions();
    }, RESIZE_THROTTLE_MS),
    [calculateDimensions]
  );

  // Set up ResizeObserver
  useEffect(() => {
    if (!parentRef.current) {
      return undefined;
    }

    calculateDimensions();

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(parentRef.current);
    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateDimensions, handleResize]);

  // Calculate rows based on total items and columns
  const rows = useMemo(() => {
    return Math.ceil(items.length / targetColumns);
  }, [items.length, targetColumns]);

  // Row virtualizer
  const rowVirtualizer = useVirtualizer({
    count: rows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemSize.height + gap.y + additionalRowGap, // Add extra padding for row spacing
    overscan,
  });

  // Column virtualizer
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: targetColumns,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemSize.width + gap.x,
    overscan,
  });

  // Re-measure when dimensions change
  useEffect(() => {
    rowVirtualizer.measure();
    columnVirtualizer.measure();
  }, [itemSize.height, itemSize.width, rowVirtualizer, columnVirtualizer]);

  // Handle scroll position persistence
  const handleScroll = useCallback(
    throttle((event: React.UIEvent<HTMLDivElement>) => {
      if (!event.currentTarget) {
        return;
      }
      const {scrollTop} = event.currentTarget;
      sessionStorage.setItem(scrollTopKey, scrollTop.toString());
    }, SCROLL_THROTTLE_MS),
    [scrollTopKey]
  );

  // Restore scroll position on mount
  useEffect(() => {
    if (!parentRef.current) {
      return undefined;
    }

    const savedScrollTop = sessionStorage.getItem(scrollTopKey);
    if (savedScrollTop) {
      // Use setTimeout to ensure the component is fully rendered
      const timeout = setTimeout(() => {
        parentRef.current?.scrollTo(0, Number(savedScrollTop));
      }, 0);

      return () => {
        clearTimeout(timeout);
      };
    }
    return undefined;
  }, [items.length, scrollTopKey]); // Re-run when items change

  // Memoize virtual items getter
  const getVirtualItems = useCallback(
    () => rowVirtualizer.getVirtualItems(),
    [rowVirtualizer]
  );

  // Memoize total size calculation
  const getTotalSize = useCallback(() => {
    return rowVirtualizer.getTotalSize();
  }, [rowVirtualizer]);

  // Render function for virtualized items
  const renderVirtualizedItems = useCallback(
    (
      renderItem: (
        item: T,
        index: number,
        virtualRow: VirtualItem<Element>,
        virtualColumn: VirtualItem<Element>
      ) => React.ReactNode
    ) => {
      return rowVirtualizer.getVirtualItems().map((virtualRow) =>
        columnVirtualizer.getVirtualItems().map((virtualColumn) => {
          const itemIndex =
            virtualRow.index * targetColumns + virtualColumn.index;
          const item = items[itemIndex];

          if (!item) {
            return null;
          }

          return React.createElement(
            'div',
            {
              key: `${virtualRow.index}-${virtualColumn.index}`,
              style: {
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${virtualColumn.size}px`,
                height: `${virtualRow.size}px`,
                transform: `translateX(${virtualColumn.start}px) translateY(${virtualRow.start}px)`,
                ...itemStyle,
              },
            },
            renderItem(item, itemIndex, virtualRow, virtualColumn)
          );
        })
      );
    },
    [rowVirtualizer, columnVirtualizer, items, targetColumns]
  );

  return {
    parentRef,
    rowVirtualizer,
    columnVirtualizer,
    itemSize,
    gap,
    columns: targetColumns,
    rows,
    handleScroll,
    getVirtualItems,
    getTotalSize,
    renderVirtualizedItems,
  };
}
